import { z } from "zod";
import { apiIntIdSchema } from "./ids.js";
import { whatsAppTemplateHeaderMediaDtoSchema } from "./whatsapp-template-header.js";
/** Derived connection state for the WhatsApp settings UI. */
export const whatsAppConnectionStateSchema = z.enum([
    "NOT_CONNECTED",
    "PENDING_PHONE_SELECTION",
    "CONNECTED",
    "DISCONNECTED",
    "ERROR",
]);
export const whatsAppVerificationCodeMethodSchema = z.enum(["SMS", "VOICE"]);
/** Where a stored pending-phone snapshot should reopen. Not a live Meta read. */
export const whatsAppVerificationResumeSchema = z.enum(["REQUEST_CODE", "VERIFY_CODE"]);
export const whatsAppPhoneRegistrationSuggestedStepSchema = z.enum([
    "REQUEST_CODE",
    "VERIFY_CODE",
    "REGISTER",
    "COMPLETE",
]);
export const whatsAppPhoneRegistrationStateSchema = z.enum([
    "needs_verification",
    "needs_registration",
    "ready_to_attach",
    "already_connected",
    "registration_processing",
]);
export const whatsAppEmbeddedSignupPhoneRegistrationSchema = z.object({
    externalPhoneNumberId: z.string().min(1),
    displayPhoneNumber: z.string().min(1),
    verifiedName: z.string().nullable(),
    registrationState: whatsAppPhoneRegistrationStateSchema,
    suggestedStep: whatsAppPhoneRegistrationSuggestedStepSchema,
    codeMethod: whatsAppVerificationCodeMethodSchema.nullable().optional(),
    codeRequestedAt: z.string().datetime().nullable().optional(),
    /** ISO timestamp copied from the per-phone request_code deadline, when one is stored. */
    retryAfter: z.string().datetime().nullable().optional(),
    /** True when a request_code attempt ended without confirmation. Not a successful send. */
    codeDeliveryUnconfirmed: z.boolean().optional(),
});
export const whatsAppPhoneNumberCandidateSchema = z.object({
    externalPhoneNumberId: z.string().min(1),
    displayPhoneNumber: z.string().min(1),
    verifiedName: z.string().nullable(),
    qualityRating: z.string().nullable().optional(),
});
export const whatsAppSelectedPhoneNumberSchema = z.object({
    id: apiIntIdSchema,
    platformEndpointId: apiIntIdSchema,
    externalPhoneNumberId: z.string().min(1),
    phoneNumber: z.string().min(1),
    displayName: z.string().nullable(),
    /** Customer-editable name on the endpoint. Null when unset. Not the Meta verified name. */
    localName: z.string().nullable(),
    status: z.enum(["PENDING", "ACTIVE", "DISABLED", "RESTRICTED"]),
    /** Live Meta CDN URL when GET business profile returns an http(s) picture. Never stored. */
    profilePictureUrl: z.string().nullable().optional(),
});
export const whatsAppConnectionPermissionsSchema = z.object({
    canConnect: z.boolean(),
    canDisconnect: z.boolean(),
    canSelectPhoneNumbers: z.boolean(),
    canCancelPending: z.boolean(),
    canSwitchWorkspacePhone: z.boolean(),
    canAddPhoneNumber: z.boolean(),
    canUpdateBusinessProfile: z.boolean().optional(),
});
export const whatsAppEmbeddedSignupConfigResponseSchema = z.object({
    enabled: z.boolean(),
    metaAppId: z.string().min(1),
    whatsappSignupConfigId: z.string().min(1),
    graphApiVersion: z.string().min(1),
    hasExistingConnection: z.boolean(),
    connectionState: whatsAppConnectionStateSchema,
    /**
     * When true, client must pass extras.featureType = whatsapp_business_app_onboarding.
     * Defaults to false so older Backend responses without the field still parse safely.
     */
    coexistenceOnboardingEnabled: z.boolean().default(false),
});
export const whatsAppConnectionResponseSchema = z.object({
    state: whatsAppConnectionStateSchema,
    platformAccountId: apiIntIdSchema.nullable(),
    accountName: z.string().nullable(),
    wabaId: z.string().nullable(),
    metaBusinessId: z.string().nullable(),
    /** All connected WhatsApp endpoints for this organization (not every WABA phone). */
    phoneNumbers: z.array(whatsAppSelectedPhoneNumberSchema),
    /** Selected workspace phone — the UI focus endpoint while connected. */
    selectedWorkspaceEndpointId: apiIntIdSchema.nullable(),
    workspacePhone: whatsAppSelectedPhoneNumberSchema.nullable(),
    permissions: whatsAppConnectionPermissionsSchema,
    /**
     * Present for PENDING_PHONE_SELECTION when a resume snapshot names one phone.
     * Derived from stored JSON. GET does not call Meta.
     */
    phoneRegistration: whatsAppEmbeddedSignupPhoneRegistrationSchema.optional(),
    verificationResume: whatsAppVerificationResumeSchema.optional(),
});
export const completeWhatsAppEmbeddedSignupRequestSchema = z.object({
    authorizationCode: z.string().min(1),
    wabaId: z.string().min(1),
    /** Optional Meta business portfolio id when the SDK provides it. */
    metaBusinessId: z.string().min(1).optional(),
    phoneNumberId: z.string().min(1).optional(),
    signupEvent: z.string().min(1).optional(),
});
export const completeWhatsAppEmbeddedSignupPendingResponseSchema = z.object({
    state: z.literal("PENDING_PHONE_SELECTION"),
    platformAccountId: apiIntIdSchema,
    wabaId: z.string().min(1),
    accountName: z.string().nullable(),
    metaBusinessId: z.string().nullable(),
    phoneNumberCandidates: z.array(whatsAppPhoneNumberCandidateSchema),
    /**
     * Present when Embedded Signup selected a phone that still needs the existing
     * verification or registration flow. The WABA credential is pending; the phone
     * is not connected.
     */
    phoneRegistration: whatsAppEmbeddedSignupPhoneRegistrationSchema.optional(),
});
export const completeWhatsAppEmbeddedSignupConnectedResponseSchema = z.object({
    state: z.literal("CONNECTED"),
    connection: whatsAppConnectionResponseSchema,
});
export const completeWhatsAppEmbeddedSignupResponseSchema = z.discriminatedUnion("state", [
    completeWhatsAppEmbeddedSignupPendingResponseSchema,
    completeWhatsAppEmbeddedSignupConnectedResponseSchema,
]);
export const selectWhatsAppPhoneNumbersRequestSchema = z.object({
    externalPhoneNumberIds: z.array(z.string().min(1)).length(1),
});
export const selectWhatsAppPhoneNumbersResponseSchema = whatsAppConnectionResponseSchema;
export const disconnectWhatsAppResponseSchema = whatsAppConnectionResponseSchema;
export const cancelPendingWhatsAppResponseSchema = whatsAppConnectionResponseSchema;
export const switchWhatsAppWorkspacePhoneRequestSchema = z.object({
    externalPhoneNumberId: z.string().min(1),
});
export const switchWhatsAppWorkspacePhoneResponseSchema = whatsAppConnectionResponseSchema;
/**
 * `connected` is a local phone a complete Meta list still includes, and the only
 * kind a sender picker may treat as a selectable connected sender.
 * A phone id omitted by that complete list is absent from the options.
 * A failed or partial Meta list does not remove a `connected` option.
 */
export const whatsAppWorkspacePhoneOptionKindSchema = z.enum([
    "connected",
    "attachable",
    "registration_required",
]);
export function isConnectedSelectableWorkspacePhone(option) {
    return option.kind === "connected";
}
export const whatsAppWorkspacePhoneOptionSchema = z.object({
    kind: whatsAppWorkspacePhoneOptionKindSchema,
    externalPhoneNumberId: z.string().min(1),
    displayPhoneNumber: z.string().min(1),
    verifiedName: z.string().nullable(),
    qualityRating: z.string().nullable().optional(),
    platformEndpointId: apiIntIdSchema.optional(),
    phoneNumber: z.string().optional(),
    status: whatsAppSelectedPhoneNumberSchema.shape.status.optional(),
    registrationState: whatsAppPhoneRegistrationStateSchema.optional(),
    suggestedStep: whatsAppPhoneRegistrationSuggestedStepSchema.optional(),
});
export const whatsAppWorkspacePhoneOptionsResponseSchema = z.object({
    currentExternalPhoneNumberId: z.string().nullable(),
    options: z.array(whatsAppWorkspacePhoneOptionSchema),
});
/** Cached WhatsApp message template (local SSOT for list UI). */
export const whatsAppTemplateDtoSchema = z.object({
    id: apiIntIdSchema,
    providerTemplateId: z.string().min(1),
    name: z.string().min(1),
    language: z.string().min(1),
    status: z.string().min(1),
    category: z.string().min(1),
    components: z.unknown(),
    syncedAt: z.string().datetime(),
    headerMedia: whatsAppTemplateHeaderMediaDtoSchema.nullable().optional(),
    /** True when HEADER format is IMAGE and Multi-Chat does not own a durable default image. */
    headerImageMissing: z.boolean().optional(),
});
export const whatsAppTemplatesListResponseSchema = z.object({
    items: z.array(whatsAppTemplateDtoSchema),
    /** WhatsAppAccount.templatesLastSyncedAt; null until a successful full sync. */
    lastSyncedAt: z.string().datetime().nullable(),
});
export const whatsAppTemplatesSyncResponseSchema = z.object({
    syncedAt: z.string().datetime(),
    syncedCount: z.number().int().nonnegative(),
    removedCount: z.number().int().nonnegative(),
    items: z.array(whatsAppTemplateDtoSchema),
});
export const whatsAppTemplateCategorySchema = z.enum(["UTILITY", "MARKETING"]);
export const whatsAppTemplateParameterFormatSchema = z.enum(["POSITIONAL", "NAMED", "NONE"]);
export const whatsAppTemplateManageParameterFormatSchema = z.enum(["POSITIONAL", "NAMED"]);
export const whatsAppTemplateButtonInputSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("QUICK_REPLY"),
        text: z.string().min(1).max(25),
    }),
    z.object({
        type: z.literal("URL"),
        text: z.string().min(1).max(25),
        url: z.string().min(1).max(2000),
        example: z.string().min(1).max(2000).optional(),
    }),
    z.object({
        type: z.literal("PHONE_NUMBER"),
        text: z.string().min(1).max(25),
        /**
         * International Multi-Chat form, e.g. +972526650754.
         * Authoritative format checks live in the API build layer so we can return
         * WHATSAPP_TEMPLATE_INVALID_PHONE_NUMBER (not generic VALIDATION_FAILED).
         */
        phoneNumber: z.string().min(1).max(32),
    }),
    z.object({
        type: z.literal("VOICE_CALL"),
        text: z.string().min(1).max(25),
        /**
         * Template-create TTL for the call CTA, in minutes.
         * Meta: 1440 (1 day) through 43200 (30 days). Overridable at send time.
         */
        ttlMinutes: z.number().int().min(1440).max(43200).optional(),
    }),
]);
export const whatsAppTemplateTextHeaderInputSchema = z.object({
    format: z.literal("TEXT").optional(),
    text: z.string().min(1).max(60),
    example: z.string().min(1).optional(),
});
export const whatsAppTemplateImageHeaderInputSchema = z.object({
    format: z.literal("IMAGE"),
});
export const whatsAppTemplateHeaderInputSchema = z.union([
    whatsAppTemplateImageHeaderInputSchema,
    whatsAppTemplateTextHeaderInputSchema,
]);
export const whatsAppTemplateComponentsInputSchema = z.object({
    header: whatsAppTemplateHeaderInputSchema.nullable().optional(),
    body: z.object({
        text: z.string().min(1).max(1024),
    }),
    footer: z
        .object({
        text: z.string().min(1).max(60),
    })
        .nullable()
        .optional(),
    buttons: z.array(whatsAppTemplateButtonInputSchema).max(10).optional(),
});
export const whatsAppTemplateExamplesInputSchema = z.object({
    header: z.string().min(1).optional(),
    body: z.union([z.array(z.string().min(1)), z.record(z.string(), z.string().min(1))]).optional(),
    urlButtons: z
        .array(z.object({
        buttonIndex: z.number().int().nonnegative(),
        value: z.string().min(1).max(2000),
    }))
        .optional(),
});
export const createWhatsAppTemplateRequestSchema = z.object({
    name: z.string().min(1).max(512),
    language: z.string().min(1),
    category: whatsAppTemplateCategorySchema,
    parameterFormat: whatsAppTemplateManageParameterFormatSchema,
    components: whatsAppTemplateComponentsInputSchema,
    examples: whatsAppTemplateExamplesInputSchema.optional(),
});
export const editWhatsAppTemplateRequestSchema = z.object({
    category: whatsAppTemplateCategorySchema.optional(),
    parameterFormat: whatsAppTemplateManageParameterFormatSchema.optional(),
    components: whatsAppTemplateComponentsInputSchema,
    examples: whatsAppTemplateExamplesInputSchema.optional(),
});
export const whatsAppTemplateMutationResponseSchema = z.object({
    item: whatsAppTemplateDtoSchema,
});
/** Normalized Calling capability for a business phone number (never raw Meta). */
export const whatsAppCallingStatusSchema = z.enum([
    "ENABLED",
    "DISABLED",
    "UNAVAILABLE",
    "UNKNOWN",
]);
export const whatsAppCallingStatusResponseSchema = z.object({
    callingStatus: whatsAppCallingStatusSchema,
});
export const whatsAppTemplateHeaderImageDownloadResponseSchema = z.object({
    url: z.string().min(1),
    expiresAt: z.string().datetime(),
    mimeType: z.string().min(1),
    fileName: z.string().min(1),
});
export const updateWhatsAppBusinessProfilePictureResponseSchema = z.object({
    success: z.literal(true),
    profilePictureUrl: z.string().nullable(),
});
/** Official WhatsApp Business Profile `vertical` write values (Cloud API v26). */
export const WHATSAPP_BUSINESS_PROFILE_VERTICALS = [
    "ALCOHOL",
    "APPAREL",
    "AUTO",
    "BEAUTY",
    "EDU",
    "ENTERTAIN",
    "EVENT_PLAN",
    "FINANCE",
    "GOVT",
    "GROCERY",
    "HEALTH",
    "HOTEL",
    "NONPROFIT",
    "ONLINE_GAMBLING",
    "OTC_DRUGS",
    "OTHER",
    "PHYSICAL_GAMBLING",
    "PROF_SERVICES",
    "RESTAURANT",
    "RETAIL",
    "TRAVEL",
];
export const whatsAppBusinessProfileVerticalSchema = z.enum(WHATSAPP_BUSINESS_PROFILE_VERTICALS);
export const WHATSAPP_DISPLAY_NAME_MIN_LENGTH = 3;
export const WHATSAPP_DISPLAY_NAME_MAX_LENGTH = 128;
export function isWhatsAppDisplayNameValid(value) {
    const trimmedLength = value.trim().length;
    return (trimmedLength >= WHATSAPP_DISPLAY_NAME_MIN_LENGTH &&
        trimmedLength <= WHATSAPP_DISPLAY_NAME_MAX_LENGTH);
}
export const createWhatsAppPhoneRequestSchema = z.object({
    countryCallingCode: z.string().regex(/^\d{1,4}$/),
    phoneNumber: z.string().regex(/^\d{4,15}$/),
    verifiedName: z
        .string()
        .trim()
        .min(WHATSAPP_DISPLAY_NAME_MIN_LENGTH)
        .max(WHATSAPP_DISPLAY_NAME_MAX_LENGTH),
    vertical: whatsAppBusinessProfileVerticalSchema,
    description: z.string().max(512).optional(),
});
export const whatsAppPhoneRegistrationStatusResponseSchema = z.object({
    externalPhoneNumberId: z.string().min(1),
    displayPhoneNumber: z.string().nullable(),
    verifiedName: z.string().nullable(),
    codeVerificationStatus: z.string().nullable(),
    nameStatus: z.string().nullable(),
    newNameStatus: z.string().nullable(),
    registrationState: whatsAppPhoneRegistrationStateSchema,
    suggestedStep: whatsAppPhoneRegistrationSuggestedStepSchema,
    /** ISO timestamp for this phone's request_code deadline, when one is stored. */
    retryAfter: z.string().datetime().nullable().optional(),
    retryAfterSource: z.enum(["application", "provider"]).nullable().optional(),
    /** True when a request_code attempt ended without confirmation. Not a successful send. */
    codeDeliveryUnconfirmed: z.boolean().optional(),
    /**
     * Last Meta-confirmed request_code, while the phone still needs verification. Says nothing
     * about how long that code stays valid.
     */
    codeRequestedAt: z.string().datetime().nullable().optional(),
    codeMethod: z.enum(["SMS", "VOICE"]).nullable().optional(),
});
export const createWhatsAppPhoneResponseSchema = whatsAppPhoneRegistrationStatusResponseSchema.extend({
    businessProfileUpdated: z.boolean(),
});
export const requestWhatsAppPhoneVerificationCodeRequestSchema = z.object({
    codeMethod: whatsAppVerificationCodeMethodSchema,
    language: z.string().min(2).max(10).optional(),
});
export const requestWhatsAppPhoneVerificationCodeResponseSchema = z.object({
    success: z.literal(true),
    retryAfter: z.string().datetime(),
    retryAfterSource: z.enum(["application", "provider"]),
});
export const verifyWhatsAppPhoneCodeRequestSchema = z.object({
    code: z.string().min(4).max(12),
});
export const verifyWhatsAppPhoneCodeResponseSchema = whatsAppPhoneRegistrationStatusResponseSchema.extend({
    success: z.literal(true),
});
export const registerWhatsAppPhoneRequestSchema = z.object({
    /** Legacy clients may still send a PIN. The server ignores it. */
    pin: z.string().regex(/^\d{6}$/).optional(),
    vertical: whatsAppBusinessProfileVerticalSchema.optional(),
    description: z.string().max(512).optional(),
});
export const registerWhatsAppPhoneResponseSchema = z.discriminatedUnion("outcome", [
    z.object({
        outcome: z.literal("connected"),
        connection: whatsAppConnectionResponseSchema,
    }),
    z.object({
        outcome: z.literal("pending"),
        registrationStatus: whatsAppPhoneRegistrationStatusResponseSchema,
    }),
]);
export const attachWhatsAppPhoneResponseSchema = whatsAppConnectionResponseSchema;
//# sourceMappingURL=whatsapp-contracts.js.map